import React, { useState, useRef, useEffect } from 'react';
import { Play, AlertCircle, CheckCircle2, Loader2, Database, X, Building2, Download, ChevronDown, FileText, FileSpreadsheet, File, Save, Bookmark, Trash2, Star, Globe, Lock } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useCustomQuery } from '../hooks/useReports';
import { savedQueriesApi, auditApi } from '../services/api';
import DataTable from './DataTable';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const sampleQueries = [
  {
    name: 'Daily Revenue',
    query: `SELECT DATE(order_date) as date, SUM(total_amount) as revenue
FROM sales
WHERE order_date >= DATEADD(day, -30, CURRENT_DATE)
GROUP BY DATE(order_date)
ORDER BY date DESC`,
  },
  {
    name: 'Top Customers',
    query: `SELECT customer_id, COUNT(*) as orders, SUM(total_amount) as total_spent
FROM sales
GROUP BY customer_id
ORDER BY total_spent DESC
LIMIT 20`,
  },
  {
    name: 'Category Performance',
    query: `SELECT category, COUNT(*) as transactions, AVG(total_amount) as avg_value
FROM sales
GROUP BY category
ORDER BY transactions DESC`,
  },
];

const tenantSpecificQueries = [
  {
    name: 'Tenant Adoption Summary',
    description: 'Daily adoption and login metrics for the selected tenant',
    query: (tenantCode) => `SELECT
  date_day,
  new_adopted_consumers,
  count_logins,
  total_adopted_consumers
FROM ref_tenant_active_days
WHERE tenant_code = '${tenantCode}'
ORDER BY date_day DESC
LIMIT 30`,
  },
  {
    name: 'Tenant Members',
    description: 'Total member count for the selected tenant',
    query: (tenantCode) => `SELECT COUNT(*) as total_members
FROM huser.consumer c
JOIN tenant.tenant t ON t.tenant_id = c.tenant_id
WHERE c.delete_nbr = 0
  AND t.tenant_code = '${tenantCode}'`,
  },
  {
    name: 'Tenant Card Activity',
    description: 'Card orders, shipments, and activations for the selected tenant',
    query: (tenantCode) => `SELECT
  co.order_date,
  COUNT(DISTINCT co.card_order_id) as cards_ordered,
  COUNT(DISTINCT CASE WHEN co.ship_date IS NOT NULL THEN co.card_order_id END) as cards_shipped,
  COUNT(DISTINCT CASE WHEN ca.activation_date IS NOT NULL THEN ca.card_id END) as cards_activated
FROM card.card_order co
JOIN tenant.tenant t ON t.tenant_id = co.tenant_id
LEFT JOIN card.card_activation ca ON ca.card_order_id = co.card_order_id
WHERE t.tenant_code = '${tenantCode}'
  AND co.order_date >= CURRENT_DATE - INTERVAL '30 days'
GROUP BY co.order_date
ORDER BY co.order_date DESC`,
  },
  {
    name: 'Tenant Cohort Membership',
    description: 'Cohort memberships for consumers in the selected tenant',
    query: (tenantCode) => `SELECT
  cc.cohort_id,
  COUNT(DISTINCT cc.consumer_code) as consumer_count
FROM cohort.cohort_consumer cc
JOIN huser.consumer c ON c.consumer_code = cc.consumer_code
JOIN tenant.tenant t ON t.tenant_id = c.tenant_id
WHERE t.tenant_code = '${tenantCode}'
  AND cc.delete_nbr = 0
  AND c.delete_nbr = 0
GROUP BY cc.cohort_id
ORDER BY consumer_count DESC`,
  },
];

const predefinedQueries = [
  {
    name: 'Tenant Details',
    description: 'View all tenants with their codes - click a tenant row to see tenant-specific queries',
    query: `SELECT tenant_name, tenant_code, partner_code FROM tenant.tenant ORDER BY tenant_name`,
    enablesTenantSelection: true,
  },
  {
    name: 'Task Complete Raw Data',
    description: 'Validate task completion raw data for a specific member ID',
    promptParam: { key: 'member_id', label: 'Member ID', placeholder: 'Enter member ID' },
    queryFn: (memberId) => `SELECT * FROM etl_staging.inbound.task_update_cus_cfa711ae720549989bacb572e8af77e1_v2_staging WHERE member_id = '${memberId}'`,
  },
  {
    name: 'SSBCI Purses that are not funded',
    description: 'Count of consumers in cohort 134 with SSBCI eligibility but without active funding purses',
    query: `SELECT COUNT(*)
FROM fis.consumer_account c

JOIN cohort.cohort_consumer cc134
  ON cc134.consumer_code = c.consumer_code
 AND cc134.cohort_id = 134

JOIN huser.consumer hc
  ON hc.consumer_code = c.consumer_code
 AND hc.delete_nbr = 0

WHERE c.delete_nbr = 0
  AND c.consumer_account_config_json IS NOT NULL

AND EXISTS (
    SELECT 1
    FROM cohort.cohort_consumer ccx
    WHERE ccx.consumer_code = c.consumer_code
      AND ccx.delete_nbr = 0
      AND ccx.cohort_id IN (94, 95)
)

/* crude JSON check */
AND NOT (
    json_extract_path_text(c.consumer_account_config_json, 'purseConfig', 'purses') LIKE '%"purseLabel":"OGT","isDeactivated":false%'
 OR json_extract_path_text(c.consumer_account_config_json, 'purseConfig', 'purses') LIKE '%"purseLabel":"CFO","isDeactivated":false%'
 OR json_extract_path_text(c.consumer_account_config_json, 'purseConfig', 'purses') LIKE '%"purseLabel":"HFO","isDeactivated":false%'
 OR json_extract_path_text(c.consumer_account_config_json, 'purseConfig', 'purses') LIKE '%"purseLabel":"HFC","isDeactivated":false%'
 OR json_extract_path_text(c.consumer_account_config_json, 'purseConfig', 'purses') LIKE '%"purseLabel":"OGP","isDeactivated":false%'
)`,
  },
];

export default function CustomQueryEditor() {
  const { isAuthenticated } = useAuth0();
  const [query, setQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isTenantSelectionMode, setIsTenantSelectionMode] = useState(false);
  const [promptInput, setPromptInput] = useState({ show: false, value: '', predefined: null });
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const { mutate, data, isPending, isError, error, isSuccess } = useCustomQuery();

  // Saved queries state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: '', description: '', isPublic: false });
  const [showSavedQueries, setShowSavedQueries] = useState(true);
  const queryClient = useQueryClient();

  // Saved queries feature disabled - auth timing issues
  const savedQueriesData = null;
  const savedQueriesLoading = false;
  const savedQueries = [];

  // Save query mutation
  const saveQueryMutation = useMutation({
    mutationFn: async () => {
      const response = await savedQueriesApi.saveQuery(
        saveForm.name,
        query,
        saveForm.description || null,
        saveForm.isPublic
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['saved-queries']);
      setShowSaveModal(false);
      setSaveForm({ name: '', description: '', isPublic: false });
    },
  });

  // Delete query mutation
  const deleteQueryMutation = useMutation({
    mutationFn: async (queryId) => {
      const response = await savedQueriesApi.deleteQuery(queryId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['saved-queries']);
    },
  });

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Export helper functions
  const getExportFileName = (extension) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    return `query_results_${timestamp}.${extension}`;
  };

  const handleExportCSV = () => {
    if (!data?.data?.length) return;

    const headers = Object.keys(data.data[0]);
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.join(','),
      ...data.data.map(row => headers.map(h => escapeCSV(row[h])).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = getExportFileName('csv');
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    setShowExportMenu(false);
  };

  const handleExportExcel = () => {
    if (!data?.data?.length) return;

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data.data);

    // Auto-size columns
    const headers = Object.keys(data.data[0]);
    const colWidths = headers.map((h) => {
      const maxLength = Math.max(
        h.length,
        ...data.data.map(row => String(row[h] || '').length)
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Query Results');
    XLSX.writeFile(wb, getExportFileName('xlsx'));
    setShowExportMenu(false);
  };

  const handleExportPDF = () => {
    if (!data?.data?.length) return;

    const doc = new jsPDF('l', 'mm', 'a4');
    const headers = Object.keys(data.data[0]);

    // Title
    doc.setFontSize(14);
    doc.setTextColor(33, 37, 41);
    doc.text('Query Results', 14, 15);

    // Metadata
    doc.setFontSize(9);
    doc.setTextColor(108, 117, 125);
    doc.text(`Exported: ${new Date().toLocaleString()}`, 14, 22);
    doc.text(`Total Rows: ${data.data.length}`, 14, 27);

    // Table
    const tableHeaders = headers.map(h => h.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
    const tableRows = data.data.map(row => headers.map(h => {
      const val = row[h];
      if (val === null || val === undefined) return '';
      const str = String(val);
      return str.length > 30 ? str.substring(0, 27) + '...' : str;
    }));

    doc.autoTable({
      head: [tableHeaders],
      body: tableRows,
      startY: 32,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 14, right: 14 },
    });

    // Footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    doc.save(getExportFileName('pdf'));
    setShowExportMenu(false);
  };

  // Log query execution to audit
  const logQueryExecution = async (queryText, queryName = 'Custom Query', rowCount = 0) => {
    try {
      await auditApi.logEvent({
        actionType: 'QUERY_EXECUTE',
        actionCategory: 'DATA_ACCESS',
        resourceType: 'custom_query',
        screenName: 'Custom Query',
        menuPath: 'Tech Support > Custom Query',
        queryType: queryName,
        searchCriteria: queryText.substring(0, 1000), // Truncate long queries
        recordsAccessed: rowCount,
        additionalData: JSON.stringify({
          queryLength: queryText.length,
          queryPreview: queryText.substring(0, 200)
        })
      });
    } catch (err) {
      console.warn('Failed to log query execution:', err);
    }
  };

  const handleExecute = () => {
    if (query.trim()) {
      mutate(query, {
        onSuccess: (result) => {
          logQueryExecution(query, 'Custom Query', result?.rowCount || 0);
        }
      });
    }
  };

  const handleSampleQuery = (sampleQuery) => {
    setQuery(sampleQuery);
  };

  const handlePredefinedQuery = (predefined) => {
    if (predefined.promptParam) {
      setPromptInput({ show: true, value: '', predefined });
      return;
    }
    setQuery(predefined.query);
    setSelectedTenant(null);
    setIsTenantSelectionMode(predefined.enablesTenantSelection || false);
    mutate(predefined.query, {
      onSuccess: (result) => {
        logQueryExecution(predefined.query, `Predefined: ${predefined.name}`, result?.rowCount || 0);
      }
    });
  };

  const handlePromptSubmit = () => {
    if (!promptInput.value.trim() || !promptInput.predefined) return;
    const generatedQuery = promptInput.predefined.queryFn(promptInput.value.trim());
    const queryName = promptInput.predefined.name;
    setQuery(generatedQuery);
    setSelectedTenant(null);
    setIsTenantSelectionMode(false);
    mutate(generatedQuery, {
      onSuccess: (result) => {
        logQueryExecution(generatedQuery, `Predefined: ${queryName}`, result?.rowCount || 0);
      }
    });
    setPromptInput({ show: false, value: '', predefined: null });
  };

  const handleTenantSelect = (tenant) => {
    setSelectedTenant({
      name: tenant.tenant_name,
      code: tenant.tenant_code,
      partnerCode: tenant.partner_code,
    });
  };

  const handleTenantSpecificQuery = (queryFn) => {
    if (!selectedTenant) return;
    const generatedQuery = queryFn(selectedTenant.code);
    setQuery(generatedQuery);
    mutate(generatedQuery, {
      onSuccess: (result) => {
        logQueryExecution(generatedQuery, `Tenant: ${selectedTenant.name}`, result?.rowCount || 0);
      }
    });
  };

  const clearTenantSelection = () => {
    setSelectedTenant(null);
    setIsTenantSelectionMode(false);
  };

  const handleLoadSavedQuery = (savedQuery) => {
    setQuery(savedQuery.query_text);
    setSelectedTenant(null);
    setIsTenantSelectionMode(false);
  };

  const handleDeleteSavedQuery = (e, queryId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this saved query?')) {
      deleteQueryMutation.mutate(queryId);
    }
  };

  const handleSaveQuery = () => {
    // Feature temporarily disabled
    alert('Save Query feature is temporarily disabled.');
  };

  const handleSaveSubmit = () => {
    if (!saveForm.name.trim()) {
      alert('Please enter a name for the query');
      return;
    }
    saveQueryMutation.mutate();
  };

  const generateColumns = (data) => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).map((key) => ({
      key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      sortable: true,
      format: typeof data[0][key] === 'number' ? 'number' : undefined,
    }));
  };

  return (
    <div className="space-y-6">
      {/* Predefined Queries Section */}
      <div className="card">
        <h3 className="card-header flex items-center gap-2">
          <Database className="w-5 h-5 text-primary-600" />
          Predefined Queries
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Click on a query to execute it immediately and view the results.
        </p>
        <div className="space-y-3">
          {predefinedQueries.map((predefined) => (
            <button
              key={predefined.name}
              onClick={() => handlePredefinedQuery(predefined)}
              disabled={isPending}
              className="w-full text-left p-4 bg-gradient-to-r from-primary-50 to-amber-50 hover:from-primary-100 hover:to-amber-100 border border-primary-200 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-primary-900">{predefined.name}</h4>
                  <p className="text-sm text-primary-700 mt-1">{predefined.description}</p>
                </div>
                <Play className="w-5 h-5 text-primary-600" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* My Saved Queries Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="card-header flex items-center gap-2 mb-0">
            <Bookmark className="w-5 h-5 text-amber-600" />
            My Saved Queries
          </h3>
          <button
            onClick={() => setShowSavedQueries(!showSavedQueries)}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            {showSavedQueries ? 'Hide' : 'Show'}
          </button>
        </div>

        {showSavedQueries && (
          <>
            {savedQueriesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : savedQueries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bookmark className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>No saved queries yet</p>
                <p className="text-sm mt-1">Write a query and click "Save Query" to save it for later</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {savedQueries.map((sq) => (
                  <div
                    key={sq.query_id}
                    onClick={() => handleLoadSavedQuery(sq)}
                    className="group relative p-4 bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 border border-amber-200 rounded-lg cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-amber-900 truncate">{sq.query_name}</h4>
                          {sq.is_public ? (
                            <Globe className="w-3.5 h-3.5 text-green-600 flex-shrink-0" title="Public query" />
                          ) : (
                            <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" title="Private query" />
                          )}
                        </div>
                        {sq.description && (
                          <p className="text-sm text-amber-700 mt-1 line-clamp-2">{sq.description}</p>
                        )}
                        <p className="text-xs text-amber-600 mt-2 font-mono truncate">
                          {sq.query_text.substring(0, 60)}...
                        </p>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          onClick={(e) => handleDeleteSavedQuery(e, sq.query_id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete query"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <Play className="w-4 h-4 text-amber-600" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Save Query Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-2 mb-4">
              <Save className="w-5 h-5 text-amber-600" />
              <h3 className="text-lg font-semibold text-gray-900">Save Query</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Query Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={saveForm.name}
                  onChange={(e) => setSaveForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Monthly Revenue Report"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={saveForm.description}
                  onChange={(e) => setSaveForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of what this query does..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={saveForm.isPublic}
                  onChange={(e) => setSaveForm(prev => ({ ...prev, isPublic: e.target.checked }))}
                  className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                />
                <label htmlFor="isPublic" className="text-sm text-gray-700">
                  Make this query public (visible to all users)
                </label>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Query Preview:</p>
                <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap max-h-24 overflow-y-auto">
                  {query.substring(0, 200)}{query.length > 200 ? '...' : ''}
                </pre>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setSaveForm({ name: '', description: '', isPublic: false });
                }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSubmit}
                disabled={!saveForm.name.trim() || saveQueryMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saveQueryMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4" /> Save Query</>
                )}
              </button>
            </div>

            {saveQueryMutation.isError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                Error: {saveQueryMutation.error?.message || 'Failed to save query'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Parameter Prompt Modal */}
      {promptInput.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {promptInput.predefined?.name}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {promptInput.predefined?.description}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {promptInput.predefined?.promptParam?.label}
            </label>
            <input
              type="text"
              value={promptInput.value}
              onChange={(e) => setPromptInput((prev) => ({ ...prev, value: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handlePromptSubmit()}
              placeholder={promptInput.predefined?.promptParam?.placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setPromptInput({ show: false, value: '', predefined: null })}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePromptSubmit}
                disabled={!promptInput.value.trim()}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play className="w-4 h-4 mr-2" />
                Execute
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="card-header">Custom SQL Query</h3>
        <p className="text-sm text-gray-500 mb-4">
          Execute read-only queries against your Redshift data warehouse. Only SELECT statements are allowed.
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Sample Queries</label>
          <div className="flex flex-wrap gap-2">
            {sampleQueries.map((sample) => (
              <button
                key={sample.name}
                onClick={() => handleSampleQuery(sample.query)}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {sample.name}
              </button>
            ))}
          </div>
        </div>

        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter your SQL query here..."
          className="w-full h-48 p-4 font-mono text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
          spellCheck="false"
        />

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-sm">
            {isPending && (
              <span className="flex items-center gap-2 text-amber-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                Executing query...
              </span>
            )}
            {isSuccess && (
              <span className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                Query executed successfully ({data?.rowCount || 0} rows)
              </span>
            )}
            {isError && (
              <span className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-4 h-4" />
                {error?.message || 'Query execution failed'}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveQuery}
              disabled={!query.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-4 h-4" />
              Save Query
            </button>
            <button
              onClick={handleExecute}
              disabled={!query.trim() || isPending}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="w-4 h-4 mr-2" />
              Execute Query
            </button>
          </div>
        </div>
      </div>

      {/* Selected Tenant Info & Tenant-Specific Queries */}
      {selectedTenant && (
        <div className="card bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-green-600" />
              <div>
                <h3 className="text-lg font-semibold text-green-900">
                  Selected Tenant: {selectedTenant.name}
                </h3>
                <p className="text-sm text-green-700">
                  Code: {selectedTenant.code} | Partner: {selectedTenant.partnerCode}
                </p>
              </div>
            </div>
            <button
              onClick={clearTenantSelection}
              className="p-2 hover:bg-green-100 rounded-lg transition-colors"
              title="Clear selection"
            >
              <X className="w-5 h-5 text-green-600" />
            </button>
          </div>

          <h4 className="font-medium text-green-900 mb-3">Tenant-Specific Queries</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tenantSpecificQueries.map((tsq) => (
              <button
                key={tsq.name}
                onClick={() => handleTenantSpecificQuery(tsq.query)}
                disabled={isPending}
                className="text-left p-3 bg-white hover:bg-green-50 border border-green-200 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-green-900">{tsq.name}</h5>
                    <p className="text-xs text-green-700 mt-1">{tsq.description}</p>
                  </div>
                  <Play className="w-4 h-4 text-green-600 flex-shrink-0 ml-2" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {isSuccess && data?.data && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="card-header mb-0">Query Results</h3>
            <div className="flex items-center gap-3">
              {isTenantSelectionMode && !selectedTenant && (
                <span className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                  Click a row to select a tenant
                </span>
              )}
              {data.data.length > 0 && (
                <div className="relative" ref={exportMenuRef}>
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                  >
                    <Download className="w-4 h-4" />
                    Export
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {showExportMenu && (
                    <div className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      <button
                        onClick={handleExportCSV}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                      >
                        <FileText className="w-4 h-4 text-green-600" />
                        Export as CSV
                      </button>
                      <button
                        onClick={handleExportExcel}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100"
                      >
                        <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                        Export as Excel
                      </button>
                      <button
                        onClick={handleExportPDF}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100 rounded-b-lg"
                      >
                        <File className="w-4 h-4 text-red-600" />
                        Export as PDF
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {isTenantSelectionMode && !selectedTenant ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {generateColumns(data.data).map((col) => (
                      <th
                        key={col.key}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.data.map((row, idx) => (
                    <tr
                      key={idx}
                      onClick={() => handleTenantSelect(row)}
                      className="hover:bg-primary-50 cursor-pointer transition-colors"
                    >
                      {generateColumns(data.data).map((col) => (
                        <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row[col.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <DataTable
              data={data.data}
              columns={generateColumns(data.data)}
              emptyMessage="Query returned no results"
            />
          )}
        </div>
      )}
    </div>
  );
}
